/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import { I18NError } from 'Utils';
import { fetchGet, fetchPut, fetchPost, fetchDelete } from 'FetchAPIs';

const messages = defineMessages({
  errorContants: 'Error fetching constants.',
  errorRemove: 'Error removing a constant value',
  errorUpdate: 'Error updating a constant value',
});

const baseConstantURL = '/api/constants';

/**
 *
 * Helper function to format data payload for constant api
 * @param {object} data - contains name and new value
 * @param {string} entityType - string of entity type request was made from
 * @param {boolean} globalConst - boolean to tell if this is a global value change
 * @param {array} constantList - array of all constant objects so we can get the rest of the values needed for payload
 * @returns {object} payload - and object containing constant for payload and the REST verb to use
 * example payload {restVerb: 'PUT', data: [{name, value, etc.}]}
 */
const dataPayload = (data, entityType, globalConst, constantList) => {
  let payload;
  constantList.forEach(constant => {
    // find the constant from list
    if (constant.name === Object.keys(data)[0]) {
      // check if update is for global or local
      if (globalConst) {
        // check if constant has entity type
        if (constant.entityType === null) {
          // its still global, make update to value and mark as PUT
          const payloadData = {
            restVerb: 'PUT',
            data: [
              {
                ...constant,
                value: {
                  value: data[Object.keys(data)[0]],
                },
              },
            ],
          };
          payload = payloadData;
        } else {
          // remove entity type, make update to value, and mark as PUT
          const payloadData = {
            restVerb: 'PUT',
            data: [
              {
                ...constant,
                entityType: null,
                value: {
                  value: data[Object.keys(data)[0]],
                },
              },
            ],
          };
          payload = payloadData;
        }
      }
      // check if constant already has entity type associated with it
      else if (constant.entityType === null) {
        // create new constant with entityType name and mark as POST
        const payloadData = {
          restVerb: 'POST',
          data: [
            {
              ...constant,
              entityType,
              value: {
                value: data[Object.keys(data)[0]],
              },
            },
          ],
        };
        payload = payloadData;
      } else {
        // if we got to here then its a local value to a constant that has already had a local override so just update and mark as PUT
        const payloadData = {
          restVerb: 'PUT',
          data: [
            {
              ...constant,
              value: {
                value: data[Object.keys(data)[0]],
              },
            },
          ],
        };
        payload = payloadData;
      }
    }
  });
  return payload;
};

export function getConstants(entityType) {
  return fetchGet(baseConstantURL, null, { entityType }).catch(error => {
    throw new I18NError(messages.errorContants, error);
  });
}

export function getGlobalConstants() {
  return fetchGet(baseConstantURL).catch(error => {
    throw new I18NError(messages.errorContants, error);
  });
}

export function updateConstant(value, entityType, globalConst, constantList, userEmailId) {
  const data = dataPayload(value, entityType, globalConst, constantList);
  // check if POST or PUT
  if (data.restVerb === 'PUT') {
    return fetchPut(baseConstantURL, data.data, { userEmailId }).catch(error => {
      throw new I18NError(messages.errorUpdate, error);
    });
  }
  return fetchPost(baseConstantURL, data.data, { userEmailId }).catch(error => {
    throw new I18NError(messages.errorUpdate, error);
  });
}

/**
 *
 * @param {*} constant constant to remove
 * @param {boolean} globalDelete set true to remove global scoped constant
 * @param {*} userEmailId
 * @param {boolean} globalDeleteOnly set true to only remove global scoped constant in payload. otherwise removes entityType-scoped and global-scoped constant in the same payload (which throws error sometimes)
 * @returns
 */
export function removeConstant(constant, globalDelete, userEmailId, globalDeleteOnly = false) {
  let payload;

  // if this is a deletion we remove both the local and global constant
  if (globalDelete) {
    if (constant.entityType) {
      const globalConstant = {
        ...constant,
        entityType: null,
      };
      if (globalDeleteOnly) {
        // used when we want to do the delete as separate calls
        payload = [globalConstant];
      } else {
        payload = [constant, globalConstant];
      }
    }
  } else {
    // otherwiser we just remove the local version to revert to global
    payload = [constant];
  }
  return fetchDelete(baseConstantURL, payload, { userEmailId }).catch(error => {
    throw new I18NError(messages.errorRemove, error);
  });
}
